import { AntDesign } from '@expo/vector-icons';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { Text } from '@/components/Text';
import { colors } from 'react-native-elements';
import { getStars } from '@/utils/timeService';
import { HighChartsRenderer } from './HighChartsRenderer';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import { logError } from '@/utils/logHandling';

let dateString;
let data;

const monthsInGerman = {
  January: 'Januar',
  February: 'Februar',
  March: 'März',
  April: 'April',
  May: 'Mai',
  June: 'Juni',
  July: 'Juli',
  August: 'August',
  September: 'September',
  October: 'Oktober',
  November: 'November',
  December: 'Dezember',
};

function sortByDate(a, b) {
  return a[0] - b[0];
}

export function ProgressCharts({ patientId }) {
  // State
  const [isLoading, setIsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [config, setConfig] = useState(null);
  const [isRendererLoaded, setIsRendererLoaded] = useState(false);

  // refs
  const rendererRef = useRef(null);
  const chartData = useRef([]);
  const zeitraum = useRef(null);
  const viewRef = useRef(null);
  const minDate = useRef(null);
  const isMounted = useRef(false);

  // variables
  const hasStars = chartData.current.length > 0 ? true : false;
  const hasBackward = useMemo(() => {
    if (
      moment()
        .subtract(weekOffset + 1, 'week')
        .valueOf() < minDate.current
    ) {
      return false;
    } else {
      return true;
    }
  }, [weekOffset, minDate.current]);
  const isForwardDisabled = weekOffset === 0;

  // methods
  const renderIfNoStars = useCallback(() => {
    return (
      <View style={styles.paddingTop}>
        <Text style={styles.textStyle}>Noch keine Sterne</Text>
      </View>
    );
  }, []);

  function generateConfig() {
    try {
      // adding the .local('de') crashes the android build
      zeitraum.current =
        moment()
          .subtract(1 + weekOffset, 'week')
          .add(1, 'day')
          .format('DD. ') +
        monthsInGerman[
          moment()
            .subtract(1 + weekOffset, 'week')
            .add(1, 'day')
            .format('MMMM')
        ] +
        ' - ' +
        moment().subtract(weekOffset, 'week').format('DD. ') +
        monthsInGerman[moment().subtract(weekOffset, 'week').format('MMMM')];
      let maxDate = 5;
      let starSize = 65;
      for (let i = 0; i < 7; i++) {
        dateString = moment()
          .subtract(i, 'day')
          .subtract(weekOffset, 'week')
          .format('YYYY-MM-DD');
        const dataKeys = Object.keys(data);
        if (dataKeys.includes(dateString)) {
          if (data[dateString] > maxDate) {
            maxDate = data[dateString];
          }
        }
      }
      maxDate++;
      viewRef.current.measure((x, y, width, measuredHeight) => {
        starSize = measuredHeight / (maxDate + 2);
        const dataConfig = {
          lang: {
            months: [
              'Januar',
              'Februar',
              'März',
              'April',
              'Mai',
              'Juni',
              'Juli',
              'August',
              'September',
              'Oktober',
              'November',
              'Dezember',
            ],
            shortDate: '%d. %b %Y', // Short date format, e.g., "10. Mai 2023"
            longDate: '%d. %B %Y', // Long date format, e.g., "10. Mai 2023"
          },
          chart: {
            type: 'spline',
            panning: false,
            animation: false,
          },
          title: {
            text: 'Übungsdauer',
          },
          xAxis: {
            type: 'datetime',
            minTickInterval: 24 * 3600 * 1000,
            min: moment()
              .subtract(1 + weekOffset, 'week')
              .subtract(4, 'hour')
              .valueOf(),
            max: moment().subtract(weekOffset, 'week').add(4, 'hour').valueOf(),
            startOnTick: false,
            endOnTick: false,
            labels: {
              style: {
                fontSize: '16',
              },
            },
          },
          yAxis: {
            tickInterval: 1,
            gridLineWidth: 0,
            title: {
              text: 'Minuten geübt',
              style: {
                fontSize: '16',
              },
            },
            labels: {
              step: 1,
              format: '{value}0',
              style: {
                fontSize: '16',
              },
            },
            min: 0,
            max: maxDate,
          },
          tooltip: {
            crosshairs: true,
            shared: true,
          },
          plotOptions: {
            spline: {
              marker: {
                enabled: true,
              },
            },
          },
          series: [
            {
              name: 'Stars',
              color: 'transparent',
              data: chartData.current,
              enableMouseTracking: false,
              allowPointSelect: false,
              marker: {
                radius: 4,
                lineColor: '#3a3a3a',
                lineWidth: 1,
                symbol:
                  'url(data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCFET0NUWVBFIHN2ZyBQVUJMSUMgIi0vL1czQy8vRFREIFNWRyAxLjEvL0VOIiAiaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkIj4KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJMYXllcl8xIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB4PSIwcHgiIHk9IjBweCIKCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgZW5hYmxlLWJhY2tncm91bmQ9Im5ldyAwIDAgNTEyIDUxMiIgeG1sOnNwYWNlPSJwcmVzZXJ2ZSI+Cjxwb2x5Z29uIGZpbGw9IiNGRkNDMzMiIHBvaW50cz0iNDgwLDIwMCAzMDguNSwyMDAgMjU2LDMyIDIwMy41LDIwMCAzMiwyMDAgMTcwLjksMzA0LjIgMTE2LDQ4MCAyNTYsMzY4IDM5Niw0ODAgMzQxLjEsMzA0LjIgIi8+Cjwvc3ZnPgo=)',
                height: starSize,
                width: starSize,
              },
            },
          ],
          legend: {
            enabled: false,
          },
          credits: {
            enabled: false,
          },
          exporting: { enabled: false },
        };
        setConfig(dataConfig);
      });
    } catch (error) {
      logError('', error);
    }
  }

  const fillCharts = async (patientId) => {
    try {
      const starData = await getStars(patientId);
      if (starData === null) {
        return setIsLoading(false);
      }
      if (Object.keys(starData).length > 0) {
        data = starData;
        const dataKeys = Object.keys(data);
        dataKeys.forEach(function (date) {
          const stars = data[date];
          for (let i = 0; i < stars; i++) {
            chartData.current.push([moment(date).valueOf(), i + 0.5]);
          }
        });
        chartData.current.sort(sortByDate);
        minDate.current = chartData.current[0][0];
        generateConfig();
      }
      setIsLoading(false);
    } catch (error) {
      logError('', error);
    }
  };

  const goBack = useCallback(() => {
    setWeekOffset((prevState) => prevState + 1);
  }, []);

  const goForward = useCallback(() => {
    setWeekOffset((prevState) => {
      if (weekOffset < 0) {
        return prevState;
      }
      return prevState - 1;
    });
  }, []);

  const onRendererLoaded = useCallback(() => {
    setIsRendererLoaded(true);
  }, []);

  const sendMessage = (data) => {
    try {
      if (Platform.OS === 'web') {
        rendererRef?.current?.contentWindow?.postMessage?.(
          JSON.stringify(data),
          '*',
        );
      } else {
        rendererRef?.current?.postMessage?.(JSON.stringify(data));
      }
    } catch (error) {
      logError('', error);
    }
  };

  // Side Effects
  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    generateConfig();
  }, [weekOffset]);

  useEffect(() => {
    if (!isRendererLoaded) {
      return;
    }
    // var dev_height = window.innerHeight;
    // height.current = dev_height * 0.9 - 150;
    fillCharts(patientId);
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, [isRendererLoaded, patientId]);

  useEffect(() => {
    if (config === null) {
      return;
    }
    sendMessage({ config });
  }, [config]);

  if (!hasStars && !isLoading) {
    return renderIfNoStars();
  }

  return (
    <View ref={viewRef} style={styles.container} collapsable={false}>
      <HighChartsRenderer
        onRendererLoaded={onRendererLoaded}
        ref={rendererRef}
      />
      <View style={styles.viewStyle}>
        <PressableNeolexon onPress={goBack} disabled={!hasBackward}>
          <AntDesign
            name="left"
            size={30}
            color={!hasBackward ? colors.disabled : colors.black}
          />
        </PressableNeolexon>
        <Text style={styles.buttonTextStyle}>{zeitraum.current}</Text>
        <PressableNeolexon onPress={goForward} disabled={isForwardDisabled}>
          <AntDesign
            name="right"
            size={30}
            color={isForwardDisabled ? colors.disabled : colors.black}
          />
        </PressableNeolexon>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  textStyle: {
    fontSize: 18,
    textAlign: 'center',
  },
  viewStyle: {
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  buttonTextStyle: {
    fontSize: 22,
    textAlign: 'center',
  },
  paddingTop: {
    paddingTop: 15,
  },
});
